export const PLAYER_ASSIGNMENT_REMOVALS_KEY = "_playerAssignmentRemovals";

export type TeamPlayerRow = {
  teamId: unknown;
  status: unknown;
  newlyAdded?: boolean;
};

export type TournamentPlayerRow = {
  tournamentId: unknown;
  status: unknown;
  newlyAdded?: boolean;
};

export type PlayerAssignmentRemovals = {
  teamPlayers: TeamPlayerRow[];
  tournamentPlayers: TournamentPlayerRow[];
};

const emptyRemovals = (): PlayerAssignmentRemovals => ({
  teamPlayers: [],
  tournamentPlayers: [],
});

function asRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter((row) => row && typeof row === "object") as Record<string, unknown>[];
}

function idsEqual(left: unknown, right: unknown): boolean {
  if (left == null || right == null) return false;
  try {
    return BigInt(left as string | number | bigint) === BigInt(right as string | number | bigint);
  } catch {
    return String(left) === String(right);
  }
}

export function filledTeamPlayers(value: unknown): TeamPlayerRow[] {
  return asRows(value)
    .filter((row) => row.teamId || row.status)
    .map((row) => ({
      teamId: row.teamId,
      status: row.status,
      newlyAdded: Boolean(row.newlyAdded),
    }));
}

export function filledTournamentPlayers(value: unknown): TournamentPlayerRow[] {
  return asRows(value)
    .filter((row) => row.tournamentId || row.status)
    .map((row) => ({
      tournamentId: row.tournamentId,
      status: row.status,
      newlyAdded: Boolean(row.newlyAdded),
    }));
}

export function hasPlayerAssignment(values: Record<string, unknown>): boolean {
  return (
    filledTeamPlayers(values.teamPlayers).length > 0 ||
    filledTournamentPlayers(values.tournamentPlayers).length > 0
  );
}

export function buildAddPlayerAssignmentsPayload(values: Record<string, unknown>): {
  teamPlayers: { teamId: unknown; status: unknown }[];
  tournamentPlayers: { tournamentId: unknown; status: unknown }[];
} {
  return {
    teamPlayers: filledTeamPlayers(values.teamPlayers).map(({ teamId, status }) => ({
      teamId,
      status,
    })),
    tournamentPlayers: filledTournamentPlayers(values.tournamentPlayers).map(
      ({ tournamentId, status }) => ({ tournamentId, status }),
    ),
  };
}

function fieldDifference<T extends Record<string, unknown>>(
  current: T[],
  baseline: T[],
  key: keyof T,
): T[] {
  return current.filter(
    (itemA) =>
      !baseline.some(
        (itemB) =>
          idsEqual(itemA[key], itemB[key]) && itemA.status === itemB.status && !itemA.newlyAdded,
      ),
  );
}

function readRemovals(values: Record<string, unknown>): PlayerAssignmentRemovals {
  const raw = values[PLAYER_ASSIGNMENT_REMOVALS_KEY];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return emptyRemovals();
  const removals = raw as Partial<PlayerAssignmentRemovals>;
  return {
    teamPlayers: Array.isArray(removals.teamPlayers) ? removals.teamPlayers : [],
    tournamentPlayers: Array.isArray(removals.tournamentPlayers) ? removals.tournamentPlayers : [],
  };
}

export function buildPlayerAssignmentsPatch(
  values: Record<string, unknown>,
  baseline: Record<string, unknown>,
): Record<string, unknown> | null {
  const currentTeams = filledTeamPlayers(values.teamPlayers);
  const baselineTeams = filledTeamPlayers(baseline.teamPlayers);
  const currentTournaments = filledTournamentPlayers(values.tournamentPlayers);
  const baselineTournaments = filledTournamentPlayers(baseline.tournamentPlayers);
  const removals = readRemovals(values);

  const teamDiff = fieldDifference(currentTeams, baselineTeams, "teamId").map(({ teamId, status }) => ({
    teamId,
    status,
  }));
  const tournamentDiff = fieldDifference(
    currentTournaments,
    baselineTournaments,
    "tournamentId",
  ).map(({ tournamentId, status }) => ({ tournamentId, status }));

  const teamPlayers = teamDiff.concat(
    removals.teamPlayers.map(({ teamId, status }) => ({ teamId, status })),
  );
  const tournamentPlayers = tournamentDiff.concat(
    removals.tournamentPlayers.map(({ tournamentId, status }) => ({ tournamentId, status })),
  );

  const patch: Record<string, unknown> = {};
  if (teamPlayers.length) patch.teamPlayers = teamPlayers;
  if (tournamentPlayers.length) patch.tournamentPlayers = tournamentPlayers;
  return Object.keys(patch).length ? patch : null;
}

export function syncPlayerAssignmentsBaseline(ctx: {
  values: Record<string, unknown>;
  baseline: Record<string, unknown>;
}): void {
  ctx.baseline.teamPlayers = JSON.parse(JSON.stringify(ctx.values.teamPlayers ?? []));
  ctx.baseline.tournamentPlayers = JSON.parse(JSON.stringify(ctx.values.tournamentPlayers ?? []));
  ctx.values[PLAYER_ASSIGNMENT_REMOVALS_KEY] = emptyRemovals();
  ctx.baseline[PLAYER_ASSIGNMENT_REMOVALS_KEY] = emptyRemovals();
}
